import {
  BannerGroup,
  isNone,
  v2_fetchBranchDeliveryMapFallback,
  v2_fetchBranchesFallback,
  v2_fetchCmsBlocksFallback,
  v2_useBanners,
  v2_useLatestRepeatableOrder,
  v2_useOrder,
  v2_useOrderRepeat,
  v2_FallbackProvider as FallbackProvider,
  v2_fetchBannersFallback,
  v2_useAuthorizedCustomer,
} from '@crocodille/common';
import { AppPage } from '@framework/page';
import PageMeta from '@framework/page-meta';
import { getAppLayoutWithCart } from '@layouts/app-layout/app-layout';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CarouselSkeleton from '../../components/carousel/carousel-skeleton';
import HorizontalSlider from '../../components/horizontal-slider/horizontal-slider';
import RepeatOrder from '../../components/order/repeat-order/repeat-order';
import ShippingPageLayout from '../../components/shipping/shipping-page-layout-2/shipping-page-layout-2';
import ShippingSelection from '../../components/shipping/shipping-selection/shipping-selection';
import * as routing from '../../routing';
import ShippingPageLayoutDynamic from '@components/shipping/shipping-page-layout-dynamic/shipping-page-layout-dynamic';
import AppModal from '@components/app-modal/app-modal';

export interface ShippingPageProps {
  readonly shipping: 'courier' | 'in-restaurant' | 'train-station';
  readonly fallback?: Partial<Record<string, unknown>>;
}

const ShippingPage: AppPage<ShippingPageProps> = ({
  shipping: initialShipping,
}) => {
  const { t } = useTranslation();
  const { query, push, locale } = useRouter();

  const shipping =
    typeof query.shipping === 'string' ? query.shipping : initialShipping;

  const handleShippingChange = useCallback(
    (shipping: routing.ShippingType) => {
      push(routing.shippingRoute({ shipping }), undefined, { shallow: true });
    },
    [push],
  );

  const { data: authorizedCustomer } = v2_useAuthorizedCustomer();
  const { data: lastListedOrder } = v2_useLatestRepeatableOrder(
    authorizedCustomer != null,
  );
  const {
    repeatOrder,
    isMutating: repeatOrderPending,
    error: repeatOrderError,
  } = v2_useOrderRepeat(lastListedOrder?.tokenValue);
  const { data: lastOrder } = v2_useOrder(lastListedOrder?.tokenValue);

  const { data: banners } = v2_useBanners(BannerGroup.HomepageTop);

  const navigateToProductList = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.href = routing.localized(locale, routing.products());
    } else {
      push(routing.products());
    }
  }, [push, locale]);

  return (
    <>
      <PageMeta
        pageTitle={t(
          shipping === 'in-restaurant'
            ? 'shipping_page.title_in_restaurant'
            : 'shipping_page.title_courier',
        )}
        description={t(
          shipping === 'in-restaurant'
            ? 'shipping_page.description_in_restaurant'
            : 'shipping_page.description_courier',
        )}
      />
      <>
        <ShippingPageLayout>
          <ShippingPageLayout.Intro isDisabled={shipping === 'in-restaurant'}>
            <ShippingSelection
              selectedShipping={shipping as routing.ShippingType}
              onSelectedShippingChange={handleShippingChange}
              navigateToProductList={navigateToProductList}
              isMainLayout
            />
          </ShippingPageLayout.Intro>

          <ShippingPageLayout.Promotions>
            {isNone(banners) || banners?.length === 0 ? (
              <CarouselSkeleton />
            ) : (
              <HorizontalSlider isDisplayControls autoAdvanceInterval={3000}>
                {banners.map((banner) => (
                  <HorizontalSlider.Slide
                    key={banner.id}
                    src={banner.image}
                    href={banner.link}
                    label={banner.name}
                  />
                ))}
              </HorizontalSlider>
            )}
          </ShippingPageLayout.Promotions>

          <ShippingPageLayout.OrderRepeat>
            {lastOrder && !lastOrder.instant ? (
              <RepeatOrder
                onRepeatOrder={async () => {
                  await repeatOrder();

                  push(routing.menu());
                }}
                lastOrder={lastOrder}
                pending={repeatOrderPending}
                error={repeatOrderError?.kind}
              />
            ) : null}
          </ShippingPageLayout.OrderRepeat>
          <ShippingPageLayoutDynamic />
        </ShippingPageLayout>
        <AppModal />
      </>
    </>
  );
};

ShippingPage.getLayout = (page, pageProps) => {
  return getAppLayoutWithCart(
    <FallbackProvider fallback={pageProps.fallback ?? {}}>
      {page}
    </FallbackProvider>,
  );
};

export const getStaticPaths: GetStaticPaths<
  routing.ShippingRouteParams & ParsedUrlQuery
> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<ShippingPageProps> = async (
  ctx,
) => {
  const shipping = ctx.params?.shipping;

  if (
    shipping !== 'in-restaurant' &&
    shipping !== 'courier' &&
    shipping !== 'train-station'
  ) {
    return {
      notFound: true,
    };
  }

  const app = getApp(ctx);

  const [
    translationProps,
    cmsBlocksFallback,
    bannersFallback,
    branchesFallback,
    branchDeliveryMapFallback,
  ] = await Promise.all([
    getServerSideTranslationProps(app, ['messages']),
    v2_fetchCmsBlocksFallback(app),
    v2_fetchBannersFallback(app, BannerGroup.HomepageTop),
    v2_fetchBranchesFallback(app),
    v2_fetchBranchDeliveryMapFallback(app),
  ]);

  const fallback = {
    ...cmsBlocksFallback,
    ...bannersFallback,
    ...branchesFallback,
    ...branchDeliveryMapFallback,
  };

  return {
    revalidate: 5 * 60, // 5 minutes
    props: {
      shipping,
      fallback,
      ...translationProps,
    },
  };
};

export default ShippingPage;
